// components/MyConsumerTab.tsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import TaskList from "./TaskList";

interface MyConsumerTabProps {
  selectedProject?: any;
  relatedBugs?: any[];
  currentTaskId?: string; // Add current task ID to identify the dependency
}

const MyConsumerTab: React.FC<MyConsumerTabProps> = ({
  selectedProject,
  relatedBugs = [],
  currentTaskId,
}) => {
  const { t } = useTranslation(["bugherd"]);

  // Transform relatedBugs to match TaskList format
  const transformedTasks = useMemo(() => {
    return relatedBugs.map((bug) => {
      // Find the dependency type for the current task
      const dependencyLink = bug.linkBugs?.find(
        (link) => link.bug === currentTaskId
      );
      const dependencyType = dependencyLink?.type || "Unknown";

      // Convert dependency type to status format
      let dependencyStatus = "Unknown";
      switch (dependencyType) {
        case "Waiting":
          dependencyStatus = "Waiting On";
          break;
        case "Blocked":
          dependencyStatus = "Blocking";
          break;
        case "Linked":
          dependencyStatus = "Linked";
          break;
        default:
          dependencyStatus = dependencyType;
      }

      return {
        ...bug,
        // Keep the original task data structure
        id: bug._id,
        projectId: bug.project?._id,
        // Add dependency information
        dependencyType: dependencyType,
        status: dependencyStatus, // This will be used to show the dependency status
        // Add a read-only flag
        isReadOnly: true,
      };
    });
  }, [relatedBugs, currentTaskId]);

  if (relatedBugs.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#666",
        }}
      >
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {t(
            "No consumer tasks found. This task is not being used as a dependency by other tasks."
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
          {t("Consumer Tasks")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("Tasks that depend on this task")} ({relatedBugs.length}{" "}
          {relatedBugs.length === 1 ? t("task") : t("tasks")})
        </Typography>
      </Box>

      {/* Task List - Read-only view */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <TaskList
          tasks={transformedTasks}
          selectedProject={null} // Pass null to show all tasks
          onUpdateTask={() => {}} // Empty function for read-only
          onDeleteTask={() => {}} // Empty function for read-only
          isReadOnly={true} // Pass read-only flag
          showDependencyStatus={true} // Show dependency status in read-only mode
        />
      </Box>
    </Box>
  );
};

export default MyConsumerTab;
